import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { jobsApi, quotesApi, statsApi, subscriptionApi, settingsApi } from '@/lib/api';
import NotificationsDropdown from '@/components/NotificationsDropdown';
import { 
  Truck, 
  MapPin, 
  Calendar,
  Package,
  DollarSign,
  Clock,
  LogOut,
  Star,
  Loader2,
  Send,
  Briefcase,
  TrendingUp,
  CheckCircle,
  CreditCard,
  AlertTriangle,
  RefreshCw,
  Shield,
  Search,
  Settings
} from 'lucide-react';

const statusColors = {
  posted: 'bg-orange-500/15 text-orange-500',
  quoted: 'bg-blue-500/15 text-blue-500',
  accepted: 'bg-green-500/15 text-green-500',
  in_progress: 'bg-yellow-500/15 text-yellow-600',
  completed: 'bg-emerald-500/15 text-emerald-600',
  cancelled: 'bg-red-500/15 text-red-500'
};

const quoteStatusColors = {
  pending: 'bg-yellow-500/15 text-yellow-600',
  accepted: 'bg-green-500/15 text-green-500',
  rejected: 'bg-red-500/15 text-red-500',
  withdrawn: 'bg-gray-500/15 text-gray-500'
};

const JobCard = ({ job, onQuote, hasQuoted }) => (
  <motion.div
    className="job-card"
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
  >
    <div className="job-card-header flex items-center justify-between">
      <h3 className="font-semibold text-base truncate pr-4" style={{ fontFamily: 'Barlow Condensed' }}>
        {job.title}
      </h3>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">{job.quote_count}/5</span>
        <Badge className={`${statusColors[job.status]} text-xs uppercase`}>
          {job.status}
        </Badge>
      </div>
    </div>
    <div className="job-card-body space-y-4">
      <p className="text-sm text-muted-foreground line-clamp-2">{job.description}</p>
      
      <div className="route-line">
        <div className="route-point">
          <p className="text-sm font-medium">{job.pickup_city}, {job.pickup_state}</p>
        </div>
        <div className="route-point destination">
          <p className="text-sm font-medium">{job.delivery_city}, {job.delivery_state}</p>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Package className="w-3.5 h-3.5" />
          {job.load_type} • {job.weight_kg} kg
        </span>
        <span className="flex items-center gap-1">
          <Calendar className="w-3.5 h-3.5" />
          Pickup: {new Date(job.pickup_date).toLocaleDateString()}
        </span>
      </div>

      <div className="pt-3 border-t border-border">
        {hasQuoted ? (
          <Badge variant="outline" className="w-full justify-center py-2">
            Quote Submitted
          </Badge>
        ) : job.quote_count >= 5 ? (
          <Badge variant="secondary" className="w-full justify-center py-2">
            Max Quotes Reached
          </Badge>
        ) : (
          <Button 
            className="w-full btn-active gap-2" 
            onClick={() => onQuote(job)}
            data-testid={`quote-btn-${job.id}`}
            data-tour="submit-quote-btn"
          >
            <Send className="w-4 h-4" />
            Submit Quote
          </Button>
        )}
      </div>
    </div>
  </motion.div>
);

const QuoteCard = ({ quote }) => (
  <motion.div
    className="quote-card"
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
  >
    <div className="flex items-start justify-between mb-3">
      <div>
        <p className="font-semibold text-lg price-display">A${quote.price.toFixed(2)}</p>
        <p className="text-xs text-muted-foreground">Job: {quote.job_id.substring(0, 8)}...</p>
      </div>
      <Badge className={`${quoteStatusColors[quote.status]} text-xs uppercase`}>
        {quote.status}
      </Badge>
    </div>
    <div className="space-y-2 text-sm text-muted-foreground">
      <p>Pickup: {quote.estimated_pickup_time}</p>
      <p>Delivery: {quote.estimated_delivery_time}</p>
      {quote.notes && <p className="italic">"{quote.notes}"</p>}
    </div>
    <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
      Submitted: {new Date(quote.created_at).toLocaleString()}
    </p>
  </motion.div>
);

export default function ProviderDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [availableJobs, setAvailableJobs] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renewingSubscription, setRenewingSubscription] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('available');
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [platformFeePercent, setPlatformFeePercent] = useState(10);
  const [quoteForm, setQuoteForm] = useState({
    price: '',
    estimated_pickup_time: '',
    estimated_delivery_time: '',
    notes: ''
  });

  useEffect(() => {
    // Check URL params for cancel action
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('cancel') === '1') {
      setShowCancelDialog(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Check if provider has active subscription OR free trial (and not cancelled)
    if (user && !user.weekly_subscription_active && (!user.free_trial_active || user.trial_cancelled)) {
      navigate('/subscribe');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [jobsRes, quotesRes, statsRes, myJobsRes, subRes, feeRes] = await Promise.all([
        jobsApi.getAvailable(),
        quotesApi.getMy(),
        statsApi.getDashboard(),
        jobsApi.getAll(),
        subscriptionApi.getMy(),
        settingsApi.getPlatformFee()
      ]);
      setAvailableJobs(jobsRes.data);
      setMyQuotes(quotesRes.data);
      setStats(statsRes.data);
      setMyJobs(myJobsRes.data.filter(j => j.accepted_provider_id === user?.id));
      setSubscription(subRes.data);
      setPlatformFeePercent(feeRes.data.fee_percent);
      
      // If subscription expired AND no free trial, redirect to renew
      if (!subRes.data.is_active && !user?.free_trial_active) {
        navigate('/subscribe');
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenQuoteDialog = (job) => {
    setSelectedJob(job);
    setQuoteForm({
      price: '',
      estimated_pickup_time: '',
      estimated_delivery_time: '',
      notes: ''
    });
    setQuoteDialogOpen(true);
  };

  const handleSubmitQuote = async () => {
    if (!quoteForm.price || !quoteForm.estimated_pickup_time || !quoteForm.estimated_delivery_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmittingQuote(true);
    try {
      await quotesApi.create({
        job_id: selectedJob.id,
        price: parseFloat(quoteForm.price),
        estimated_pickup_time: quoteForm.estimated_pickup_time,
        estimated_delivery_time: quoteForm.estimated_delivery_time,
        notes: quoteForm.notes
      });
      toast.success('Quote submitted successfully!');
      setQuoteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit quote');
    } finally {
      setSubmittingQuote(false);
    }
  };

  const quotedJobIds = new Set(myQuotes.map(q => q.job_id));

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleRenewSubscription = async () => {
    setRenewingSubscription(true);
    try {
      const response = await subscriptionApi.renew(window.location.origin);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to renew subscription');
      setRenewingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      const response = await subscriptionApi.cancel();
      toast.success(response.data.message);
      setShowCancelDialog(false);
      fetchData(); // Refresh subscription status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      const response = await subscriptionApi.reactivate();
      toast.success(response.data.message);
      fetchData(); // Refresh subscription status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await subscriptionApi.getCustomerPortal();
      window.location.href = response.data.portal_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to open subscription management. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container-app py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/thm-logo.png" 
                alt="The Hotshot Marketplace" 
                className="h-14 w-auto"
              />
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="font-medium">{user?.rating?.toFixed(1) || '0.0'}</span>
              <span className="text-muted-foreground">({user?.total_reviews || 0})</span>
            </div>
            <NotificationsDropdown />
            <div className="flex items-center gap-3 pl-4 border-l border-border">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">Provider</p>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container-app py-8">
        {/* Subscription Alert Banner - Only show for paid subscriptions, not free trials */}
        {subscription && !subscription.free_trial_active && subscription.days_remaining <= 2 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-sm border flex items-center justify-between ${
              subscription.days_remaining === 0 
                ? 'bg-red-500/10 border-red-500/30' 
                : 'bg-yellow-500/10 border-yellow-500/30'
            }`}
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-5 h-5 ${subscription.days_remaining === 0 ? 'text-red-500' : 'text-yellow-500'}`} />
              <div>
                <p className="font-medium">
                  {subscription.days_remaining === 0 
                    ? 'Your subscription expires today!' 
                    : `Your subscription expires in ${subscription.days_remaining} day${subscription.days_remaining > 1 ? 's' : ''}`}
                </p>
                <p className="text-sm text-muted-foreground">Renew now to continue accessing jobs</p>
              </div>
            </div>
            <Button 
              onClick={handleRenewSubscription}
              disabled={renewingSubscription}
              className="btn-active gap-2"
              data-testid="renew-banner-btn"
            >
              {renewingSubscription ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Renew Now
            </Button>
          </motion.div>
        )}

        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            PROVIDER DASHBOARD
          </h1>
          <p className="text-muted-foreground">
            Find loads, submit quotes, and grow your business
          </p>
        </div>

        {/* Subscription/Trial Card */}
        <motion.div 
          className={`mb-8 bg-card border rounded-sm p-6 ${
            subscription?.trial_cancelled ? 'border-yellow-500/30' : 'border-border'
          }`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          data-tour="trial-status"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                subscription?.trial_cancelled ? 'bg-yellow-500/10' : 'bg-primary/10'
              }`}>
                <CreditCard className={`w-6 h-6 ${
                  subscription?.trial_cancelled ? 'text-yellow-500' : 'text-primary'
                }`} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ fontFamily: 'Barlow Condensed' }}>
                  {subscription?.free_trial_active && !subscription?.weekly_subscription_active 
                    ? 'FREE TRIAL' 
                    : 'SUBSCRIPTION'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {subscription?.trial_cancelled ? (
                    <span className="text-yellow-600">
                      Cancelled • Access until {subscription.free_trial_expires_at 
                        ? new Date(subscription.free_trial_expires_at).toLocaleDateString('en-AU')
                        : 'period ends'}
                    </span>
                  ) : subscription?.free_trial_active && !subscription?.weekly_subscription_active ? (
                    <>
                      <span className="text-green-600 font-medium">Active</span> • {subscription.trial_days_remaining} day{subscription.trial_days_remaining !== 1 ? 's' : ''} remaining
                      {subscription.free_trial_expires_at && (
                        <span className="ml-2 text-xs">
                          (Ends: {new Date(subscription.free_trial_expires_at).toLocaleDateString('en-AU')})
                        </span>
                      )}
                    </>
                  ) : subscription?.is_active ? (
                    <>
                      Active • {subscription.days_remaining} day{subscription.days_remaining !== 1 ? 's' : ''} remaining
                    </>
                  ) : (
                    'Inactive'
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {subscription?.free_trial_active && !subscription?.weekly_subscription_active && (
                <span className="text-sm px-2 py-1 bg-green-500/10 text-green-600 rounded">
                  Free Trial
                </span>
              )}
              {!subscription?.trial_cancelled && (
                <>
                  <span className="text-lg font-bold price-display">A${subscription?.price || '9.95'}/week</span>
                  {subscription?.free_trial_active && !subscription?.weekly_subscription_active ? (
                    <>
                      {subscription?.has_payment_method && (
                        <Button 
                          variant="outline"
                          onClick={handleManageSubscription}
                          className="gap-2"
                          data-testid="manage-subscription-btn"
                        >
                          <Settings className="w-4 h-4" />
                          Manage
                        </Button>
                      )}
                      <Button 
                        variant="outline"
                        onClick={() => setShowCancelDialog(true)}
                        className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/10"
                        data-testid="cancel-subscription-btn"
                      >
                        Cancel Trial
                      </Button>
                    </>
                  ) : (
                    <>
                      {subscription?.has_payment_method && (
                        <Button 
                          variant="outline"
                          onClick={handleManageSubscription}
                          className="gap-2"
                          data-testid="manage-subscription-btn"
                        >
                          <Settings className="w-4 h-4" />
                          Manage
                        </Button>
                      )}
                      <Button 
                        variant={subscription?.days_remaining <= 2 ? 'default' : 'outline'}
                        onClick={handleRenewSubscription}
                        disabled={renewingSubscription}
                        className="gap-2"
                        data-testid="renew-subscription-btn"
                      >
                        {renewingSubscription ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        Renew
                      </Button>
                    </>
                  )}
                </>
              )}
              {subscription?.trial_cancelled && (
                <Button 
                  variant="default"
                  onClick={handleReactivateSubscription}
                  className="gap-2"
                  data-testid="reactivate-subscription-btn"
                >
                  Reactivate
                </Button>
              )}
              {/* Show Add Payment Method if no card on file */}
              {!subscription?.has_payment_method && !subscription?.trial_cancelled && (
                <Link to="/subscribe?add_card=true">
                  <Button 
                    variant="default"
                    className="gap-2"
                    data-testid="add-payment-method-btn"
                  >
                    <CreditCard className="w-4 h-4" />
                    Add Payment Method
                  </Button>
                </Link>
              )}
            </div>
          </div>
          {subscription?.free_trial_active && !subscription?.trial_cancelled && !subscription?.weekly_subscription_active && (
            <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
              After your trial ends, you'll need to subscribe at $9.95/week to continue accessing jobs. 
              The 10% platform fee applies when you complete jobs.
            </p>
          )}
        </motion.div>

        {/* Verification Card - Show if not verified */}
        {user?.verification_status !== 'verified' && (
          <motion.div 
            className={`mb-8 bg-card border rounded-sm p-6 ${
              user?.verification_status === 'under_review' 
                ? 'border-blue-500/30' 
                : user?.verification_status === 'rejected'
                ? 'border-red-500/30'
                : 'border-yellow-500/30'
            }`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                  user?.verification_status === 'under_review'
                    ? 'bg-blue-500/10'
                    : user?.verification_status === 'rejected'
                    ? 'bg-red-500/10'
                    : 'bg-yellow-500/10'
                }`}>
                  <Shield className={`w-6 h-6 ${
                    user?.verification_status === 'under_review'
                      ? 'text-blue-500'
                      : user?.verification_status === 'rejected'
                      ? 'text-red-500'
                      : 'text-yellow-500'
                  }`} />
                </div>
                <div>
                  <h3 className="font-semibold" style={{ fontFamily: 'Barlow Condensed' }}>
                    ACCOUNT VERIFICATION
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {user?.verification_status === 'under_review' 
                      ? 'Your documents are being reviewed (24-48 hours)'
                      : user?.verification_status === 'rejected'
                      ? 'Verification rejected - please resubmit documents'
                      : 'Verify your account to build customer trust'}
                  </p>
                </div>
              </div>
              <Link to="/verification">
                <Button 
                  variant={user?.verification_status === 'rejected' ? 'destructive' : 'outline'}
                  className="gap-2"
                  data-testid="verification-btn"
                >
                  <Shield className="w-4 h-4" />
                  {user?.verification_status === 'under_review' 
                    ? 'View Status' 
                    : user?.verification_status === 'rejected'
                    ? 'Resubmit'
                    : 'Get Verified'}
                </Button>
              </Link>
            </div>
          </motion.div>
        )}

        {/* Verified Badge - Show if verified */}
        {user?.verification_status === 'verified' && (
          <motion.div 
            className="mb-8 bg-green-500/5 border border-green-500/30 rounded-sm p-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm font-medium text-green-600">
                Verified Provider - Your account is verified and trusted by customers
              </span>
            </div>
          </motion.div>
        )}

        {/* Stripe Connect Payout Card */}
        <motion.div 
          className={`mb-8 bg-card border rounded-sm p-6 ${
            user?.stripe_connect_status === 'active' 
              ? 'border-green-500/30' 
              : 'border-yellow-500/30'
          }`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                user?.stripe_connect_status === 'active'
                  ? 'bg-green-500/10'
                  : 'bg-yellow-500/10'
              }`}>
                <DollarSign className={`w-6 h-6 ${
                  user?.stripe_connect_status === 'active'
                    ? 'text-green-500'
                    : 'text-yellow-500'
                }`} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ fontFamily: 'Barlow Condensed' }}>
                  PAYOUT SETTINGS
                </h3>
                <p className="text-sm text-muted-foreground">
                  {user?.stripe_connect_status === 'active' 
                    ? 'Bank account connected - automatic payouts enabled'
                    : 'Connect your bank to receive automatic payouts'}
                </p>
              </div>
            </div>
            <Link to="/provider/payouts">
              <Button 
                variant={user?.stripe_connect_status === 'active' ? 'outline' : 'default'}
                className={user?.stripe_connect_status !== 'active' ? 'btn-active gap-2' : 'gap-2'}
                data-testid="payout-settings-btn"
                data-tour="setup-payouts"
              >
                <DollarSign className="w-4 h-4" />
                {user?.stripe_connect_status === 'active' 
                  ? 'Manage Payouts' 
                  : 'Setup Payouts'}
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="dashboard-grid mb-8" data-tour="provider-stats">
          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
                <Send className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="stat-value" data-testid="total-quotes-stat">{stats?.total_quotes || 0}</p>
                <p className="stat-label">Quotes Sent</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/10 rounded-sm flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="stat-value" data-testid="accepted-quotes-stat">{stats?.accepted_quotes || 0}</p>
                <p className="stat-label">Jobs Won</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-500/10 rounded-sm flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <p className="stat-value" data-testid="active-jobs-stat">{stats?.active_jobs || 0}</p>
                <p className="stat-label">Active Jobs</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="stat-card"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/10 rounded-sm flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="stat-value price-display" data-testid="total-earned-stat">
                  A${stats?.total_earned?.toLocaleString() || '0'}
                </p>
                <p className="stat-label">Total Earned</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <TabsList>
              <TabsTrigger value="available" data-testid="tab-available" data-tour="available-jobs">
                Available Jobs ({availableJobs.length})
              </TabsTrigger>
              <TabsTrigger value="quotes" data-testid="tab-quotes" data-tour="my-quotes-tab">
                My Quotes ({myQuotes.length})
              </TabsTrigger>
              <TabsTrigger value="active" data-testid="tab-active" data-tour="my-jobs-tab">
                My Jobs ({myJobs.length})
              </TabsTrigger>
            </TabsList>
            
            <Link to="/jobs/browse">
              <Button variant="outline" size="sm" data-testid="browse-all-jobs-btn">
                <Search className="w-4 h-4 mr-2" />
                Browse All Jobs
              </Button>
            </Link>
          </div>

          <TabsContent value="available">
            {availableJobs.length === 0 ? (
              <div className="text-center py-16 bg-card border border-border rounded-sm">
                <div className="w-16 h-16 bg-muted rounded-sm flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  NO AVAILABLE JOBS
                </h3>
                <p className="text-muted-foreground">
                  Check back later for new job postings
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {availableJobs.map((job) => (
                  <JobCard 
                    key={job.id} 
                    job={job} 
                    onQuote={handleOpenQuoteDialog}
                    hasQuoted={quotedJobIds.has(job.id)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="quotes">
            {myQuotes.length === 0 ? (
              <div className="text-center py-16 bg-card border border-border rounded-sm">
                <div className="w-16 h-16 bg-muted rounded-sm flex items-center justify-center mx-auto mb-4">
                  <Send className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  NO QUOTES YET
                </h3>
                <p className="text-muted-foreground">
                  Submit your first quote on an available job
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {myQuotes.map((quote) => (
                  <QuoteCard key={quote.id} quote={quote} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="active">
            {myJobs.length === 0 ? (
              <div className="text-center py-16 bg-card border border-border rounded-sm">
                <div className="w-16 h-16 bg-muted rounded-sm flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  NO ACTIVE JOBS
                </h3>
                <p className="text-muted-foreground">
                  Win a quote to see your active jobs here
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {myJobs.map((job) => (
                  <JobCard 
                    key={job.id} 
                    job={job} 
                    onQuote={() => navigate(`/job/${job.id}`)}
                    hasQuoted={true}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Quote Dialog */}
      <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              SUBMIT QUOTE
            </DialogTitle>
            <DialogDescription>
              {selectedJob?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Fee Reminder Alert */}
            <div className={`p-3 border rounded-sm ${platformFeePercent === 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
              <div className="flex items-start gap-2 text-sm">
                <DollarSign className={`w-4 h-4 mt-0.5 flex-shrink-0 ${platformFeePercent === 0 ? 'text-green-500' : 'text-amber-600'}`} />
                <div>
                  <p className={`font-medium mb-1 ${platformFeePercent === 0 ? 'text-green-600' : 'text-amber-600'}`}>
                    {platformFeePercent === 0 ? '🎉 Promotion Active - 0% Fee!' : '⚠️ Important: Platform Fee'}
                  </p>
                  <p className="text-muted-foreground">
                    {platformFeePercent === 0 
                      ? 'You receive 100% of the quoted price during this promotion!'
                      : `A ${platformFeePercent}% platform fee will be deducted from your payout when the job is completed.`
                    }
                    {quoteForm.price && parseFloat(quoteForm.price) > 0 && (
                      <span className="block mt-1 font-medium text-foreground">
                        Your payout: A${(parseFloat(quoteForm.price) * (1 - platformFeePercent / 100)).toFixed(2)} (from A${parseFloat(quoteForm.price).toFixed(2)} quoted)
                      </span>
                    )}
                  </p>
                  {platformFeePercent > 0 && (
                    <p className="text-amber-700 mt-2 font-semibold bg-amber-500/10 p-2 rounded-sm">
                      💡 Make sure to include the {platformFeePercent}% fee in your quote price to protect your earnings. By submitting a quote, you acknowledge this fee.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2" data-tour="quote-price">
              <Label htmlFor="price">Your Price (A$)</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={quoteForm.price}
                onChange={(e) => setQuoteForm({ ...quoteForm, price: e.target.value })}
                data-testid="quote-price-input"
              />
            </div>
            
            <div className="space-y-2" data-tour="quote-pickup-time">
              <Label htmlFor="pickup_time">Estimated Pickup Time</Label>
              <Input
                id="pickup_time"
                placeholder="e.g., Within 2 hours"
                value={quoteForm.estimated_pickup_time}
                onChange={(e) => setQuoteForm({ ...quoteForm, estimated_pickup_time: e.target.value })}
                data-testid="quote-pickup-input"
              />
            </div>
            
            <div className="space-y-2" data-tour="quote-delivery-time">
              <Label htmlFor="delivery_time">Estimated Delivery Time</Label>
              <Input
                id="delivery_time"
                placeholder="e.g., Same day by 6 PM"
                value={quoteForm.estimated_delivery_time}
                onChange={(e) => setQuoteForm({ ...quoteForm, estimated_delivery_time: e.target.value })}
                data-testid="quote-delivery-input"
              />
            </div>
            
            <div className="space-y-2" data-tour="quote-notes">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Any additional information..."
                value={quoteForm.notes}
                onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
                data-testid="quote-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuoteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitQuote} 
              disabled={submittingQuote}
              className="btn-active"
              data-testid="submit-quote-btn"
              data-tour="submit-quote-confirm"
            >
              {submittingQuote ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Quote
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
              CANCEL YOUR SUBSCRIPTION?
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p>Are you sure you want to cancel your free trial?</p>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-3 text-sm">
                <p className="font-medium text-yellow-600">What happens when you cancel:</p>
                <ul className="text-muted-foreground mt-2 space-y-1">
                  <li>• You'll keep access until your trial ends</li>
                  <li>• You won't be charged anything</li>
                  <li>• You can still complete active jobs</li>
                  <li>• You can reactivate anytime before trial ends</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Keep My Trial
            </Button>
            <Button 
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={cancellingSubscription}
              data-testid="confirm-cancel-btn"
            >
              {cancellingSubscription ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Yes, Cancel'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
